import React, { useMemo, useState } from "react";
import Button from "../../../components/ui/Button";
import type { SelectChangeEvent } from "../../../components/ui/Select";
import Select from "../../../components/ui/Select";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";

interface PropertySelectorProps {
	onPropertyChange?: (value: string) => void;
	onDateRangeChange?: (value: string) => void;
	onExport?: () => void;
}

const PropertySelector: React.FC<PropertySelectorProps> = ({
	onPropertyChange,
	onDateRangeChange,
	onExport,
}) => {
	const [selectedProperty, setSelectedProperty] = useState("all");
	const [dateRange, setDateRange] = useState("30days");

	// Fetch properties from API
	const { data: propertiesData } = useListPropertiesQuery({
		page_size: 1000,
	});

	// Build property options from API data
	const propertyOptions = useMemo(() => {
		const options = [{ value: "all", label: "All Properties" }];
		if (propertiesData?.results) {
			propertiesData.results.forEach((property) => {
				options.push({
					value: property.id,
					label: `${property.name} - ${property.address}`,
				});
			});
		}
		return options;
	}, [propertiesData]);

	const dateRangeOptions = [
		{ value: "7days", label: "Last 7 days" },
		{ value: "30days", label: "Last 30 days" },
		{ value: "90days", label: "Last 90 days" },
		{ value: "year", label: "This year" },
		{ value: "custom", label: "Custom range" },
	];

	const handlePropertyChange = (event: SelectChangeEvent): void => {
		const value = event.target.value as string;
		setSelectedProperty(value);
		onPropertyChange?.(value);
	};

	const handleDateRangeChange = (event: SelectChangeEvent): void => {
		const value = event.target.value as string;
		setDateRange(value);
		onDateRangeChange?.(value);
	};

	return (
		<div className="bg-card border border-border rounded-lg p-4 card-shadow mb-6">
			<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4">
				<div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
					<div className="w-full sm:w-64">
						<Select
							label="Property"
							options={propertyOptions}
							value={selectedProperty}
							onChange={handlePropertyChange}
							className="w-full"
						/>
					</div>

					<div className="w-full sm:w-48">
						<Select
							label="Date Range"
							options={dateRangeOptions}
							value={dateRange}
							onChange={handleDateRangeChange}
							className="w-full"
						/>
					</div>
				</div>

				<div className="flex items-center space-x-2 w-full sm:w-auto">
					<Button
						variant="outline"
						iconName="RefreshCw"
						iconPosition="left"
						onClick={() => window.location.reload()}
						className="flex-1 sm:flex-none"
					>
						Refresh
					</Button>

					<Button
						variant="default"
						iconName="Download"
						iconPosition="left"
						onClick={onExport}
						className="flex-1 sm:flex-none"
					>
						Export
					</Button>
				</div>
			</div>
		</div>
	);
};

export default PropertySelector;
